import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import { TestStatus } from '../constants/test-status';

interface ActivityLogEntry {
  id: number;
  type: string;
  courseId: number | null;
  courseName: string | null;
  levelId: number | null;
  levelName: string | null;
  createdAt: string;
}

interface UserCourseProgress {
  courseId: number;
  courseName: string;
  isDeleted: boolean;
  totalLevels: number;
  completedLevels: number;
  totalPoints: number;
}

interface UserStatsOverview {
  user: { id: number; name: string; surname: string };
  totalPoints: number;
  currentStreak: number;
  longestStreak: number;
  perfectLevelsCount: number;
  badgesEarnedCount: number;
  averageTestScore: number | null;
  recentActivity: ActivityLogEntry[];
  courseProgress: UserCourseProgress[];
}

interface PaginatedActivity {
  activities: ActivityLogEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface PaginatedCourses {
  courses: UserCourseProgress[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface UserTestEntry {
  id: number;
  startedAt: string;
  endedAt: string;
  score: number;
  status: TestStatus;
  isGenerated: boolean;
}

interface PaginatedTests {
  tests: UserTestEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

async function fetchUserStats(
  userId: number,
  organisationId: number,
): Promise<UserStatsOverview> {
  const response = await apiFetch(
    ENDPOINTS.USER_STATS_OVERVIEW(userId, organisationId),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch user stats');
  }

  return response.json();
}

async function fetchUserActivity(
  userId: number,
  organisationId: number,
  page: number,
  limit: number,
): Promise<PaginatedActivity> {
  const response = await apiFetch(
    ENDPOINTS.USER_STATS_ACTIVITY(userId, organisationId, page, limit),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch user activity');
  }

  return response.json();
}

async function fetchUserCourses(
  userId: number,
  organisationId: number,
  page: number,
  limit: number,
  search?: string,
): Promise<PaginatedCourses> {
  const response = await apiFetch(
    ENDPOINTS.USER_STATS_COURSES(userId, organisationId, page, limit, search),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch user courses');
  }

  return response.json();
}

interface PersonCourseDetail {
  courseId: number;
  courseName: string;
  totalLevels: number;
  completedLevels: number;
  totalPoints: number;
  levels: {
    levelId: number;
    levelName: string;
    levelNumber: number;
    isCompleted: boolean;
    scorePercent: number | null;
  }[];
}

interface TestSummary {
  totalTests: number;
  lastTestDate: string | null;
  averageScore: number | null;
  isLastTestGenerated: boolean;
}

interface PersonDetail {
  courses: PersonCourseDetail[];
  testSummary: TestSummary;
}

async function fetchPersonDetail(
  organizationId: number,
  userId: number,
): Promise<PersonDetail> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_STATS_PERSON_DETAIL(organizationId, userId),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch person detail');
  }

  return response.json();
}

async function fetchUserTests(
  userId: number,
  organisationId: number,
  page: number,
  limit: number,
): Promise<PaginatedTests> {
  const response = await apiFetch(
    ENDPOINTS.USER_STATS_TESTS(userId, organisationId, page, limit),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch user tests');
  }

  return response.json();
}

export function useUserStats(
  userId: number | undefined,
  organisationId: number | undefined,
) {
  return useQuery({
    queryKey: ['user', userId, 'stats', organisationId],
    queryFn: () => fetchUserStats(userId!, organisationId!),
    enabled: !!userId && !!organisationId,
  });
}

export function useUserActivity(
  userId: number | undefined,
  organisationId: number | undefined,
  page: number,
  limit: number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['user', userId, 'activity', organisationId, page, limit],
    queryFn: () => fetchUserActivity(userId!, organisationId!, page, limit),
    enabled: !!userId && !!organisationId && enabled,
  });
}

export function useUserCourses(
  userId: number | undefined,
  organisationId: number | undefined,
  page: number,
  limit: number,
  search: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['user', userId, 'courses', organisationId, page, limit, search],
    queryFn: () =>
      fetchUserCourses(userId!, organisationId!, page, limit, search),
    enabled: !!userId && !!organisationId && enabled,
  });
}

export function useUserTests(
  userId: number | undefined,
  organisationId: number | undefined,
  page: number,
  limit: number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['user', userId, 'tests', organisationId, page, limit],
    queryFn: () => fetchUserTests(userId!, organisationId!, page, limit),
    enabled: !!userId && !!organisationId && enabled,
  });
}

export function usePersonDetail(
  organizationId: number | undefined,
  userId: number | undefined,
  enabled: boolean = false,
) {
  return useQuery({
    queryKey: ['organization', organizationId, 'people', userId, 'detail'],
    queryFn: () => fetchPersonDetail(organizationId!, userId!),
    enabled: !!organizationId && !!userId && enabled,
  });
}

export type {
  UserStatsOverview,
  ActivityLogEntry,
  UserCourseProgress,
  UserTestEntry,
  PersonCourseDetail,
  PersonDetail,
  TestSummary,
  PaginatedActivity,
  PaginatedCourses,
  PaginatedTests,
};
